// Shared Settings page — works for every role.
// Sections (top→bottom): Profile, Sign-in, Password, Notifications,
// WhatsApp, Timezone, 2FA, Marketing, Account info.

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Save, ShieldCheck, AlertCircle, MessageCircle, Bell, Globe2,
  KeyRound, Loader2, Check, Copy, Smartphone,
} from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell, type NavSection } from '../components/shared/PortalShell';
import { Card, SectionHeader } from '../components/shared/primitives';
import { updateMe, twoFactor, type Me, type UpdateMeInput, type TwoFactorEnableResponse } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLowData } from '../contexts/LowDataContext';
import { Button } from '../components/stripe-ui/Button';
import { Modal } from '../components/shared/Modal';
import { CUSTOMER_NAV, MERCHANT_NAV, ADMIN_NAV, RECIPIENT_NAV } from './_navs';

const NAV_BY_ROLE: Record<string, NavSection[]> = {
  customer:    CUSTOMER_NAV,
  recipient:   RECIPIENT_NAV,
  merchant:    MERCHANT_NAV,
  admin:       ADMIN_NAV,
  super_admin: ADMIN_NAV,
};

// A trimmed Olson tz list — covers Haiti + biggest diaspora regions.
const TIMEZONES = [
  'UTC',
  'America/Port-au-Prince',
  'America/New_York',
  'America/Toronto',
  'America/Chicago',
  'America/Los_Angeles',
  'America/Santo_Domingo',
  'America/Miami',
  'America/Montreal',
  'Europe/Paris',
  'Europe/London',
  'Europe/Brussels',
];

export function SettingsPage() {
  const { user, refresh } = useAuth();
  if (!user) return null;

  const nav = NAV_BY_ROLE[user.role] ?? CUSTOMER_NAV;
  return (
    <PortalShell nav={nav} activeId="settings" title="Settings" subtitle="Your profile, sign-in, security, and notifications.">
      <div className="max-w-2xl space-y-5">
        <ProfileSection user={user} onSaved={refresh} />
        <SignInSection user={user} onSaved={refresh} />
        <PasswordSection />
        <NotificationsSection user={user} onSaved={refresh} />
        <WhatsAppSection user={user} onSaved={refresh} />
        <TimezoneSection user={user} onSaved={refresh} />
        <DataSaverSection />
        <TwoFactorSection user={user} onSaved={refresh} />
        <MarketingSection user={user} onSaved={refresh} />
        <AccountInfoSection user={user} />
      </div>
    </PortalShell>
  );
}

// ── Profile ────────────────────────────────────────────────

function ProfileSection({ user, onSaved }: { user: Me; onSaved: () => void }) {
  const cp = (user.customer_profile  ?? {}) as { full_name?: string; country?: string };
  const mp = (user.merchant_profile  ?? {}) as { business_name?: string; legal_name?: string };
  const rp = (user.recipient_profile ?? {}) as { full_name?: string };
  const ap = (user.admin_profile     ?? {}) as { display_name?: string };

  const initialName =
    user.role === 'customer'  ? (cp.full_name ?? '')
  : user.role === 'recipient' ? (rp.full_name ?? '')
  : user.role === 'admin' || user.role === 'super_admin' ? (ap.display_name ?? '')
  : '';

  const [fullName,     setFullName]     = useState(initialName);
  const [country,      setCountry]      = useState(cp.country ?? '');
  const [businessName, setBusinessName] = useState(mp.business_name ?? '');
  const [legalName,    setLegalName]    = useState(mp.legal_name ?? '');

  const save = useMutation({
    mutationFn: () => {
      const input: UpdateMeInput = {};
      if (fullName.trim()) input.full_name = fullName.trim();
      if (user.role === 'customer') input.country = country.trim() || undefined;
      if (user.role === 'merchant') {
        if (businessName.trim()) input.business_name = businessName.trim();
        if (legalName.trim() !== (mp.legal_name ?? '')) input.legal_name = legalName.trim();
      }
      return updateMe(input);
    },
    onSuccess: async () => { await onSaved(); toast.success('Profile updated'); },
    onError: (e: Error) => toast.error(e.message || 'Save failed'),
  });

  return (
    <Card>
      <SectionHeader title="Profile" subtitle="How you appear in VoyeLakay." />
      <div className="space-y-3">
        {user.role !== 'merchant' && (
          <Labeled label={user.role === 'admin' || user.role === 'super_admin' ? 'Display name' : 'Full name'}>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputCls} />
          </Labeled>
        )}
        {user.role === 'customer' && (
          <Labeled label="Country" hint="ISO 2-letter (US, CA, FR, HT, …)">
            <input value={country} onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))} className={inputCls} maxLength={2} />
          </Labeled>
        )}
        {user.role === 'merchant' && (
          <>
            <Labeled label="Business name"><input value={businessName} onChange={(e) => setBusinessName(e.target.value)} className={inputCls} /></Labeled>
            <Labeled label="Legal name" hint="Optional — name on your registration documents."><input value={legalName} onChange={(e) => setLegalName(e.target.value)} className={inputCls} /></Labeled>
          </>
        )}
        <div className="pt-2">
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={save.isPending}>
            <Save className="w-3.5 h-3.5 mr-1" /> Save profile
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ── Sign-in (email + phone + locale) ───────────────────────

function SignInSection({ user, onSaved }: { user: Me; onSaved: () => void }) {
  const [email,  setEmail]  = useState(user.email ?? '');
  const [phone,  setPhone]  = useState(user.phone_e164 ?? '');
  const [locale, setLocale] = useState<'en'|'fr'|'ht'>((user.locale as 'en'|'fr'|'ht') ?? 'en');

  const save = useMutation({
    mutationFn: () => updateMe({
      email:      email.trim() || undefined,
      phone_e164: phone.trim() || undefined,
      locale,
    }),
    onSuccess: async () => { await onSaved(); toast.success('Sign-in updated'); },
    onError: (e: Error) => toast.error(e.message || 'Save failed'),
  });

  return (
    <Card>
      <SectionHeader title="Sign-in" subtitle="Where we reach you." />
      <div className="space-y-3">
        <Labeled label="Email"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} /></Labeled>
        <Labeled label="Phone" hint="E.164 with country code"><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} placeholder="+1…" /></Labeled>
        <Labeled label="Language">
          <select value={locale} onChange={(e) => setLocale(e.target.value as 'en'|'fr'|'ht')} className={inputCls}>
            <option value="en">English</option>
            <option value="fr">Français</option>
            <option value="ht">Kreyòl</option>
          </select>
        </Labeled>
        <div className="pt-2">
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={save.isPending}>
            <Save className="w-3.5 h-3.5 mr-1" /> Save sign-in
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ── Password ───────────────────────────────────────────────

function PasswordSection() {
  const [current, setCurrent] = useState('');
  const [next,    setNext]    = useState('');
  const [confirm, setConfirm] = useState('');

  const save = useMutation({
    mutationFn: () => {
      if (next.length < 8) throw new Error('New password must be at least 8 characters.');
      if (next !== confirm) throw new Error('Confirmation does not match.');
      return updateMe({ current_password: current, password: next, password_confirmation: confirm });
    },
    onSuccess: () => { toast.success('Password changed'); setCurrent(''); setNext(''); setConfirm(''); },
    onError: (e: Error) => toast.error(e.message || 'Change failed'),
  });

  return (
    <Card>
      <SectionHeader title="Password" subtitle="Change your sign-in password." />
      <div className="space-y-3">
        <Labeled label="Current password"><input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className={inputCls} autoComplete="current-password" /></Labeled>
        <div className="grid grid-cols-2 gap-3">
          <Labeled label="New password"><input type="password" value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} autoComplete="new-password" /></Labeled>
          <Labeled label="Confirm new"><input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} autoComplete="new-password" /></Labeled>
        </div>
        <div className="pt-2">
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={save.isPending || !current || !next}>
            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Change password
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ── Notifications ──────────────────────────────────────────

function NotificationsSection({ user, onSaved }: { user: Me; onSaved: () => void }) {
  const [emailOn,    setEmailOn]    = useState(user.notify_email);
  const [smsOn,      setSmsOn]      = useState(user.notify_sms);
  const [whatsappOn, setWhatsappOn] = useState(user.notify_whatsapp);
  const [inAppOn,    setInAppOn]    = useState(user.notify_in_app);

  const save = useMutation({
    mutationFn: () => updateMe({
      notify_email:    emailOn,
      notify_sms:      smsOn,
      notify_whatsapp: whatsappOn,
      notify_in_app:   inAppOn,
    }),
    onSuccess: async () => { await onSaved(); toast.success('Notifications updated'); },
    onError: (e: Error) => toast.error(e.message || 'Save failed'),
  });

  return (
    <Card>
      <SectionHeader title="Notifications" subtitle="Pick where we send transactional alerts (quotes, payments, delivery confirmations)." />
      <div className="space-y-2">
        <Toggle icon={Bell}            label="Email"            on={emailOn}    onChange={setEmailOn} />
        <Toggle icon={Smartphone}      label="SMS"              on={smsOn}      onChange={setSmsOn} />
        <Toggle icon={MessageCircle}   label="WhatsApp"         on={whatsappOn} onChange={setWhatsappOn} hint="Uses your WhatsApp number below." />
        <Toggle icon={Bell}            label="In-app"           on={inAppOn}    onChange={setInAppOn} />
        <div className="pt-2">
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            <Save className="w-3.5 h-3.5 mr-1" /> Save preferences
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ── WhatsApp ───────────────────────────────────────────────

function WhatsAppSection({ user, onSaved }: { user: Me; onSaved: () => void }) {
  const [wa, setWa] = useState(user.whatsapp_e164 ?? user.phone_e164 ?? '');
  const save = useMutation({
    mutationFn: () => updateMe({ whatsapp_e164: wa.trim() || undefined, notify_whatsapp: true }),
    onSuccess: async () => { await onSaved(); toast.success('WhatsApp number saved'); },
    onError: (e: Error) => toast.error(e.message || 'Save failed'),
  });

  return (
    <Card>
      <SectionHeader title="WhatsApp" subtitle="Many HT diaspora keep a separate WhatsApp number. Set it here so quote nudges + delivery alerts land on the right line." />
      <Labeled label="WhatsApp number" hint="E.164 with country code; leave blank to use the phone number above.">
        <input type="tel" value={wa} onChange={(e) => setWa(e.target.value)} className={inputCls} placeholder="+509…" />
      </Labeled>
      <div className="pt-3">
        <Button onClick={() => save.mutate()} loading={save.isPending}>
          <MessageCircle className="w-3.5 h-3.5 mr-1" /> Save WhatsApp
        </Button>
      </div>
    </Card>
  );
}

// ── Timezone ───────────────────────────────────────────────

function TimezoneSection({ user, onSaved }: { user: Me; onSaved: () => void }) {
  const [tz, setTz] = useState(user.timezone ?? 'UTC');
  const save = useMutation({
    mutationFn: () => updateMe({ timezone: tz }),
    onSuccess: async () => { await onSaved(); toast.success('Timezone saved'); },
    onError: (e: Error) => toast.error(e.message || 'Save failed'),
  });

  return (
    <Card>
      <SectionHeader title="Time zone" subtitle="Used for delivery windows + receipt timestamps." />
      <Labeled label="Time zone">
        <select value={tz} onChange={(e) => setTz(e.target.value)} className={inputCls}>
          {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Labeled>
      <div className="pt-3">
        <Button onClick={() => save.mutate()} loading={save.isPending}>
          <Globe2 className="w-3.5 h-3.5 mr-1" /> Save time zone
        </Button>
      </div>
    </Card>
  );
}

// ── Data saver ─────────────────────────────────────────────

/**
 * Low-data mode.
 *
 * Recipients and merchants in Haiti are frequently on 3G or a metered bundle,
 * where a handful of delivery photos is a noticeable share of a monthly plan.
 *
 * It DEFERS images rather than hiding them: a delivery photo is how a sender
 * verifies goods arrived and is the evidence in a dispute, so it stays one tap
 * away. Stored per device, because a display preference belongs to the phone,
 * not the account — a shared family handset should not inherit it.
 */
function DataSaverSection() {
  const { lowData, toggle } = useLowData();

  return (
    <Card>
      <SectionHeader
        title="Data saver"
        subtitle="For slow or metered connections. Photos load only when you tap them."
      />
      <div className="flex items-start justify-between gap-4 py-1">
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold text-slate-900">Don't load photos automatically</p>
          <p className="mt-0.5 text-[11.5px] leading-relaxed text-slate-500">
            Delivery photos and other images show as a placeholder you can tap.
            Nothing is hidden — codes, statuses and order details always load.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={lowData}
          aria-label="Data saver"
          onClick={toggle}
          className={`relative h-6 w-11 shrink-0 rounded-full transition ${lowData ? 'bg-brand-600' : 'bg-slate-300'}`}
        >
          <span
            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${lowData ? 'left-[22px]' : 'left-0.5'}`}
          />
        </button>
      </div>
    </Card>
  );
}

// ── Two-factor authentication ──────────────────────────────

function TwoFactorSection({ user, onSaved }: { user: Me; onSaved: () => void }) {
  const statusQ = useQuery({
    queryKey: ['me', '2fa'],
    queryFn:  () => twoFactor.status(),
    initialData: { enabled: !!user.two_factor_enabled_at, enabled_at: user.two_factor_enabled_at },
  });
  const enabled = statusQ.data?.enabled ?? false;

  const [setup,   setSetup]   = useState<TwoFactorEnableResponse | null>(null);
  const [code,    setCode]    = useState('');
  const [pwd,     setPwd]     = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisable, setShowDisable] = useState(false);

  const enableMut = useMutation({
    mutationFn: () => twoFactor.enable(),
    onSuccess: (r) => setSetup(r),
    onError: (e: Error) => toast.error(e.message || 'Could not start setup'),
  });
  const confirmMut = useMutation({
    mutationFn: () => twoFactor.confirm(code),
    onSuccess: async () => {
      toast.success('2FA enabled');
      setSetup(null); setCode('');
      statusQ.refetch();
      await onSaved();
    },
    onError: (e: Error) => toast.error(e.message || 'Wrong code'),
  });
  const disableMut = useMutation({
    mutationFn: () => twoFactor.disable(pwd, disableCode),
    onSuccess: async () => {
      toast.success('2FA disabled');
      setShowDisable(false); setPwd(''); setDisableCode('');
      statusQ.refetch();
      await onSaved();
    },
    onError: (e: Error) => toast.error(e.message || 'Disable failed'),
  });

  return (
    <Card>
      <SectionHeader
        title="Two-factor authentication"
        subtitle="Adds a 6-digit code on top of your password. Use Google Authenticator, 1Password, Authy, or any TOTP app."
      />
      <div className="flex items-center gap-3">
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-semibold ${
          enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
        }`}>
          {enabled ? <Check className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
          {enabled ? 'Enabled' : 'Not enabled'}
        </span>
        {statusQ.data?.enabled_at && (
          <span className="text-[11.5px] text-slate-500">since {new Date(statusQ.data.enabled_at).toLocaleDateString()}</span>
        )}
      </div>

      <div className="mt-4 flex gap-2">
        {!enabled && !setup && (
          <Button onClick={() => enableMut.mutate()} loading={enableMut.isPending}>
            <KeyRound className="w-3.5 h-3.5 mr-1" /> Set up 2FA
          </Button>
        )}
        {enabled && (
          <Button tone="danger" onClick={() => setShowDisable(true)}>
            <KeyRound className="w-3.5 h-3.5 mr-1" /> Disable
          </Button>
        )}
      </div>

      {/* Enable modal */}
      {setup && (
        <Modal
          open
          onClose={() => { setSetup(null); setCode(''); }}
          title="Scan, then enter the 6-digit code"
          subtitle="Scan with Google Authenticator / 1Password / Authy"
          width="md"
          footer={
            <>
              <Button tone="secondary" onClick={() => { setSetup(null); setCode(''); }}>Cancel</Button>
              <Button onClick={() => confirmMut.mutate()} loading={confirmMut.isPending} disabled={confirmMut.isPending || code.length !== 6}>
                Verify & enable
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <div className="flex justify-center bg-white rounded-lg border border-slate-100 p-3">
              <div className="w-56 h-56" dangerouslySetInnerHTML={{ __html: setup.qr_svg }} />
            </div>
            <div className="text-center">
              <p className="text-[11.5px] text-slate-500">Or paste this key manually:</p>
              <div className="mt-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-50 font-mono text-[12px]">
                {setup.secret}
                <button type="button" onClick={() => { navigator.clipboard.writeText(setup.secret); toast.success('Secret copied'); }} className="text-slate-400 hover:text-slate-700">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <Labeled label="6-digit code from your app">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className={inputCls + ' text-center text-[18px] font-mono tracking-[0.4em]'}
                maxLength={6}
                inputMode="numeric"
                placeholder="123456"
              />
            </Labeled>
            <RecoveryCodesPanel codes={setup.recovery_codes} />
          </div>
        </Modal>
      )}

      {/* Disable modal */}
      {showDisable && (
        <Modal
          open
          onClose={() => setShowDisable(false)}
          title="Disable 2FA?"
          subtitle="You'll go back to password-only sign-in."
          width="md"
          footer={
            <>
              <Button tone="secondary" onClick={() => setShowDisable(false)}>Cancel</Button>
              <Button tone="danger" onClick={() => disableMut.mutate()} loading={disableMut.isPending} disabled={!pwd || !disableCode}>
                Disable
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <Labeled label="Your password"><input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className={inputCls} /></Labeled>
            <Labeled label="6-digit code OR a recovery code" hint="Use either your authenticator app or one of your saved recovery codes.">
              <input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} className={inputCls + ' font-mono'} />
            </Labeled>
          </div>
        </Modal>
      )}
    </Card>
  );
}

function RecoveryCodesPanel({ codes }: { codes: string[] }) {
  const copy = () => { navigator.clipboard.writeText(codes.join('\n')); toast.success('Recovery codes copied'); };
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3">
      <p className="text-[12px] font-semibold text-amber-900 inline-flex items-center gap-1">
        <AlertCircle className="w-3.5 h-3.5" /> Save these recovery codes somewhere safe
      </p>
      <p className="mt-0.5 text-[11.5px] text-amber-800">Each works once if you lose your phone. We won't show them again.</p>
      <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-[11.5px] text-slate-700">
        {codes.map((c) => <span key={c}>{c}</span>)}
      </div>
      <button type="button" onClick={copy} className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-amber-900 hover:underline">
        <Copy className="w-3 h-3" /> Copy all
      </button>
    </div>
  );
}

// ── Marketing opt-in ───────────────────────────────────────

function MarketingSection({ user, onSaved }: { user: Me; onSaved: () => void }) {
  const [optIn, setOptIn] = useState(user.marketing_opt_in);
  const save = useMutation({
    mutationFn: () => updateMe({ marketing_opt_in: optIn }),
    onSuccess: async () => { await onSaved(); toast.success(optIn ? 'You\'re subscribed' : 'Unsubscribed'); },
    onError: (e: Error) => toast.error(e.message || 'Save failed'),
  });
  return (
    <Card>
      <SectionHeader title="Marketing & updates" subtitle="Occasional emails about new categories, merchants, or features. Off by default — opt in to hear from us." />
      <Toggle icon={Bell} label="Send me marketing emails" on={optIn} onChange={setOptIn} />
      <div className="pt-3">
        <Button onClick={() => save.mutate()} loading={save.isPending}>
          <Save className="w-3.5 h-3.5 mr-1" /> Save preference
        </Button>
      </div>
    </Card>
  );
}

// ── Account info (read-only) ───────────────────────────────

function AccountInfoSection({ user }: { user: Me }) {
  return (
    <Card>
      <SectionHeader title="Account" subtitle="Read-only info." />
      <dl className="grid grid-cols-2 gap-3 text-[13px]">
        <Row label="User ID" value={String(user.id)} mono />
        <Row label="Role" value={user.role} />
        <Row label="Status" value={user.status} />
        <Row label="Locale" value={user.locale} />
        <Row label="Timezone" value={user.timezone} />
      </dl>
      {user.status !== 'active' && (
        <p className="mt-3 text-[12px] text-amber-700 inline-flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5" /> Account status: {user.status}. Some actions may be restricted.
        </p>
      )}
    </Card>
  );
}

// ── Shared bits ────────────────────────────────────────────

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</dt>
      <dd className={mono ? 'font-mono text-slate-900' : 'text-slate-900 capitalize'}>{value}</dd>
    </>
  );
}

function Toggle({
  icon: Icon, label, on, onChange, hint,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  on: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <label className="flex items-center justify-between gap-3 py-1.5 cursor-pointer">
      <span className="flex items-start gap-3">
        <Icon className="w-4 h-4 text-slate-400 mt-0.5" />
        <span>
          <span className="block text-[13px] font-medium text-slate-900">{label}</span>
          {hint && <span className="block text-[11px] text-slate-500 mt-0.5">{hint}</span>}
        </span>
      </span>
      <button
        type="button"
        onClick={() => onChange(!on)}
        role="switch"
        aria-checked={on}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
          on ? 'bg-brand-600' : 'bg-slate-200'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            on ? 'translate-x-4.5 translate-x-[18px]' : 'translate-x-[2px]'
          }`}
        />
      </button>
    </label>
  );
}

const inputCls = 'w-full px-3 py-2 text-[14px] bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400';

function Labeled({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
        {label}{hint && <span className="text-[11px] text-slate-400 normal-case ml-1 font-normal">· {hint}</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Loading() {
  return <Loader2 className="w-4 h-4 animate-spin text-slate-400" />;
}

// (kept in export to satisfy "Loading is unused" linters in case it gets re-used)
void Loading;
